import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { termsPrivacyService } from '../services/termsPrivacyService';

interface PolicyViewModalProps {
  isOpen: boolean;
  onClose: () => void;
  type: 'terms' | 'privacy';
}

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 10001;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
`;

const ModalContent = styled.div`
  background: var(--color-surface);
  border-radius: 12px;
  padding: 24px;
  max-width: 800px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  border: var(--modal-border);
  box-shadow: var(--modal-shadow-large);
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const ModalTitle = styled.h2`
  color: var(--color-text-primary);
  margin: 0;
  font-size: 1.5rem;
  font-weight: bold;
  text-align: center;

  /* 라이트 모드에서는 검정색, 다크 모드에서는 흰색 */
  :root[data-theme='light'] & {
    color: var(--color-text-primary);
  }

  :root:not([data-theme='light']) & {
    color: #ffffff;
  }
`;

const PolicyContent = styled.div`
  padding: 16px;
  background: var(--color-bg);
  border-radius: 8px;
  border: 1px solid var(--color-border);
  line-height: 1.6;
  color: var(--color-text-secondary);
  text-align: left;

  /* HTML 콘텐츠에도 다크모드 스타일 적용 */
  :root:not([data-theme='light']) & * {
    color: #ffffff !important;
  }

  p {
    margin: 0 0 12px 0;
    text-align: left;

    /* 라이트 모드에서는 검정색, 다크 모드에서는 흰색 */
    :root[data-theme='light'] & {
      color: var(--color-text-secondary);
    }

    :root:not([data-theme='light']) & {
      color: #ffffff;
    }
  }

  h2 {
    font-size: 1.1rem;
    font-weight: 600;
    color: var(--color-text-primary);
    margin: 16px 0 8px 0;
    text-align: left;

    /* 라이트 모드에서는 검정색, 다크 모드에서는 흰색 */
    :root[data-theme='light'] & {
      color: var(--color-text-primary);
    }

    :root:not([data-theme='light']) & {
      color: #ffffff;
    }
  }

  h3 {
    font-size: 1rem;
    font-weight: 600;
    color: var(--color-text-primary);
    margin: 12px 0 6px 0;
    text-align: left;

    /* 라이트 모드에서는 검정색, 다크 모드에서는 흰색 */
    :root[data-theme='light'] & {
      color: var(--color-text-primary);
    }

    :root:not([data-theme='light']) & {
      color: #ffffff;
    }
  }

  ul, ol {
    margin: 8px 0;
    padding-left: 24px;

    /* 라이트 모드에서는 검정색, 다크 모드에서는 흰색 */
    :root[data-theme='light'] & {
      color: var(--color-text-secondary);
    }

    :root:not([data-theme='light']) & {
      color: #ffffff;
    }
  }

  li {
    margin: 4px 0;

    /* 라이트 모드에서는 검정색, 다크 모드에서는 흰색 */
    :root[data-theme='light'] & {
      color: var(--color-text-secondary);
    }

    :root:not([data-theme='light']) & {
      color: #ffffff;
    }
  }

  /* 본문은 왼쪽 정렬 (메인 타이틀만 모달에서 중앙) */
  & * {
    text-align: left;
  }

  /* 추가: 모든 HTML 요소에 다크모드 색상 적용 */
  :root:not([data-theme='light']) & p,
  :root:not([data-theme='light']) & h1,
  :root:not([data-theme='light']) & h2,
  :root:not([data-theme='light']) & h3,
  :root:not([data-theme='light']) & h4,
  :root:not([data-theme='light']) & h5,
  :root:not([data-theme='light']) & h6,
  :root:not([data-theme='light']) & span,
  :root:not([data-theme='light']) & div,
  :root:not([data-theme='light']) & strong,
  :root:not([data-theme='light']) & em,
  :root:not([data-theme='light']) & li {
    color: #ffffff !important;
  }
`;

const LoadingText = styled.div`
  text-align: center;
  color: var(--color-text-secondary);
  padding: 40px;

  /* 라이트 모드에서는 검정색, 다크 모드에서는 흰색 */
  :root[data-theme='light'] & {
    color: var(--color-text-secondary);
  }

  :root:not([data-theme='light']) & {
    color: #ffffff;
  }
`;

const CloseButton = styled.button`
  padding: 12px 24px;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  background: var(--color-accent-strong);
  color: white;
  align-self: center;
  min-width: 120px;
  
  &:hover {
    background: var(--color-accent);
  }
  
  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(var(--color-accent-rgb), 0.3);
  }
`;

const PolicyViewModal: React.FC<PolicyViewModalProps> = ({ isOpen, onClose, type }) => {
  const [content, setContent] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);

  const loadContent = useCallback(async () => {
    setLoading(true);
    try {
      if (type === 'terms') {
        const data = await termsPrivacyService.getTermsOfUse();
        setContent(data?.content || '<p>서비스 이용약관 내용이 없습니다.</p>');
      } else {
        const data = await termsPrivacyService.getPrivacyPolicy();
        setContent(data?.content || '<p>개인정보처리방침 내용이 없습니다.</p>');
      }
    } catch (error) {
      console.error('약관 로드 실패:', error);
      setContent(type === 'terms' 
        ? '<p>서비스 이용약관을 불러오는 데 실패했습니다.</p>'
        : '<p>개인정보처리방침을 불러오는 데 실패했습니다.</p>');
    } finally {
      setLoading(false);
    }
  }, [type]);

  useEffect(() => {
    if (isOpen) {
      loadContent();
    } else {
      setContent('');
    }
  }, [isOpen, loadContent]);

  if (!isOpen) return null;

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalTitle>
          {type === 'terms' ? '서비스 이용약관' : '개인정보처리방침'}
        </ModalTitle>
        <PolicyContent>
          {loading ? (
            <LoadingText>로딩 중...</LoadingText>
          ) : (
            <div dangerouslySetInnerHTML={{ __html: content }} />
          )}
        </PolicyContent>
        <CloseButton onClick={onClose}>닫기</CloseButton>
      </ModalContent>
    </ModalOverlay>
  );
};

export default PolicyViewModal;

